import { useMemo, useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@apollo/client";
import BdrCheckbox from "../../components/BdrCheckbox/BdrCheckbox";
import { GROUP_ORDER, TOKEN_META } from "./designTokenMeta";
import { PAGE_REGISTRY } from "./pageRegistry";
import {
  MY_DESIGN_DRAFT,
  UPDATE_MY_DESIGN_DRAFT,
  PUBLISH_MY_DESIGN,
} from "./designOperations";
import { MockPreview } from "./MockPreview";
import { makeKit } from "./mockPrimitives";
import { renderMock } from "./surfaceMocks";
import Auth from "../../utils/auth";
import { hasAnyRole } from "../../utils/permissions";

// Which surfaces a viewer may see/theme = what their role actually sees in the
// app. Refinisher-only users get shared + refinisher (NEVER admin pages like
// Reports/Payroll); admin/office/developer see everything. Mirrors the app's
// role-based navigation (locked plan decision).
function allowedRolesFor(permissionStr) {
  const privileged = hasAnyRole(permissionStr, ["admin", "developer", "office"]);
  return privileged
    ? ["shared", "refinisher", "admin", "office"]
    : ["shared", "refinisher"];
}

// ── recents / saved swatches (localStorage) — ported from the BoSS console.
const LS_RECENTS = "bdr_designer_recents";
const LS_SAVED = "bdr_designer_swatches";
const loadLS = (k, d) => {
  try {
    const v = JSON.parse(localStorage.getItem(k));
    return v ?? d;
  } catch {
    return d;
  }
};
const saveLS = (k, v) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {
    /* ignore */
  }
};
const normHex = (h) =>
  /^#[0-9a-fA-F]{6}$/.test(h || "") ? h.toUpperCase() : null;
const pushFront = (arr, hex, cap) => {
  const h = normHex(hex);
  if (!h) return arr;
  return [h, ...arr.filter((x) => x !== h)].slice(0, cap);
};

export const cssColor = (hex, alpha) => {
  if (!normHex(hex)) return "#000";
  if (alpha == null || alpha === 255) return hex;
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${(alpha / 255).toFixed(3)})`;
};
const checker =
  "repeating-conic-gradient(#ccc 0% 25%, #f5f5f5 0% 50%) 50% / 10px 10px";

// ── contrast guard (warn-only, publish-time) ─────────────────────────────────
// REGRESSION check, not an absolute rule (BoSS lesson: absolute thresholds
// block legitimate defaults — textMuted/surfaceCard ships at 4.11:1). A pair
// warns only when the edited ratio is BOTH below its default-baseline ratio
// AND below 4.5:1 (strict < on both, so untouched defaults never warn).
// Hex-only by design: alpha compositing needs a defined backdrop the app
// doesn't have one of — an alpha-only edit therefore bypasses the guard,
// accepted for a warn-only feature.
const relLum = (hex) => {
  const ch = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
};
const contrastRatio = (a, b) => {
  const la = relLum(a);
  const lb = relLum(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
};
const CONTRAST_PAIRS = [
  ["textPrimary", "surfaceCard"],
  ["textPrimary", "surfaceModal"],
  // Schedule Time Off + Personal Event dialogs (2026-09-11) paint their own
  // surface token; guard it like the generic modal.
  ["textPrimary", "surfaceScheduleModal"],
  ["textPrimary", "surfaceSheet"],
  // Was ["textOnLight", "drawerBg"] — a pair the app never painted: textOnLight
  // appears nowhere in app_drawer.dart. The generic white card it was really
  // describing is surfaceLight (split out of drawerBg 2026-07-30), and the
  // drawer's own foregrounds are the drawer* tokens below. Guarding a pair that
  // does not exist is how a white-on-white drawer shipped with the guard green.
  ["textOnLight", "surfaceLight"],
  ["drawerText", "drawerBg"],
  ["drawerIcon", "drawerBg"],
  ["drawerSectionLabel", "drawerBg"],
  ["drawerChevron", "drawerBg"],
  ["drawerChevronCollapsed", "drawerBg"],
  // Deliberately NOT guarded: ["drawerNotesText", "drawerNotesBg"]. The guard is
  // hex-only (see normHex below) and drawerNotesText is white on a mid-tone
  // fill — the real ratio is 2.81:1, accepted per plan D4. Listing it would
  // report a pass or a spurious fail depending on alpha handling; better to
  // omit it than to encode a number the guard cannot actually compute.
  ["labelText", "inputFill"],
  ["textMuted", "surfaceCard"],
  ["textOnPrimary", "accentPrimary"],
];
const contrastWarnings = (colors) => {
  const out = [];
  if (!colors) return out;
  for (const [fg, bg] of CONTRAST_PAIRS) {
    const f = colors[fg];
    const b = colors[bg];
    if (!f || !b) continue;
    if (!normHex(f.hex) || !normHex(b.hex) || !normHex(f.defaultHex) || !normHex(b.defaultHex)) continue;
    const edited = contrastRatio(f.hex, b.hex);
    const baseline = contrastRatio(f.defaultHex, b.defaultHex);
    if (edited < baseline && edited < 4.5) out.push({ fg, bg, edited, baseline });
  }
  return out;
};

// dirty snapshot = non-default globals + all page overrides, canonicalized.
const normState = (colors, pages) =>
  JSON.stringify({
    g: Object.entries(colors)
      .filter(([, c]) => !c.isDefault)
      .map(([t, c]) => ({ t, h: c.hex, a: c.alpha ?? null }))
      .sort((a, b) => a.t.localeCompare(b.t)),
    p: Object.entries(pages)
      .map(([pid, toks]) => ({
        pid,
        c: Object.entries(toks)
          .map(([t, c]) => ({ t, h: c.hex, a: c.alpha ?? null }))
          .sort((a, b) => a.t.localeCompare(b.t)),
      }))
      .filter((x) => x.c.length)
      .sort((a, b) => a.pid.localeCompare(b.pid)),
  });

export default function DesignerPage() {
  const { data, loading, refetch } = useQuery(MY_DESIGN_DRAFT, {
    fetchPolicy: "network-only",
  });
  const [updateDraft, { loading: saving }] = useMutation(UPDATE_MY_DESIGN_DRAFT);
  const [publish, { loading: publishing }] = useMutation(PUBLISH_MY_DESIGN);

  const [colors, setColors] = useState(null); // token -> {hex,alpha,isDefault,defaultHex,defaultAlpha}
  const [pages, setPages] = useState({}); // pageId -> { token -> {hex, alpha} }  (sparse)
  const [savedSnapshot, setSavedSnapshot] = useState("");
  const [tab, setTab] = useState("global"); // global | pages | pageStyles
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [confirming, setConfirming] = useState(false);
  // 'resetAll' | {clearPage: pageId} | null — destructive-action confirm modal
  const [confirmingReset, setConfirmingReset] = useState(null);
  const [recents, setRecents] = useState(() => loadLS(LS_RECENTS, []));
  const [savedSwatches, setSavedSwatches] = useState(() => loadLS(LS_SAVED, []));
  const [activeToken, setActiveToken] = useState(null);
  const [highlightToken, setHighlightToken] = useState(null); // hovered row → glow in preview
  const [filterToPage, setFilterToPage] = useState(false); // "only colors on this preview"

  const hydrate = (d) => {
    if (!d) return;
    const cmap = {};
    for (const c of d.globals || [])
      cmap[c.token] = {
        hex: c.hex,
        alpha: c.alpha,
        isDefault: c.isDefault,
        defaultHex: c.defaultHex,
        defaultAlpha: c.defaultAlpha,
      };
    const pmap = {};
    for (const p of d.pages || []) {
      pmap[p.pageId] = {};
      for (const c of p.colors || [])
        pmap[p.pageId][c.token] = { hex: c.hex, alpha: c.alpha };
    }
    setColors(cmap);
    setPages(pmap);
    setSavedSnapshot(normState(cmap, pmap));
  };
  useEffect(() => {
    if (data?.myDesignDraft) hydrate(data.myDesignDraft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const localDirty = colors ? normState(colors, pages) !== savedSnapshot : false;

  // Unsaved-edits guard: a long recoloring session (or a misclicked reset-all)
  // shouldn't be lost silently to a tab close / refresh.
  useEffect(() => {
    if (!localDirty) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [localDirty]);

  // ── global edits ──
  const setGlobal = (token, patch, commitHex) => {
    setColors((p) => ({ ...p, [token]: { ...p[token], ...patch, isDefault: false } }));
    if (commitHex) pushRecent(commitHex);
  };
  const resetGlobal = (token) =>
    setColors((p) => ({
      ...p,
      [token]: { ...p[token], hex: p[token].defaultHex, alpha: p[token].defaultAlpha, isDefault: true },
    }));
  // Reset EVERY global back to its server-provided default. Page overrides are
  // deliberately kept (they have their own page-wide reset). Nothing hits the
  // server until Save/Publish — before saving, a page reload restores the draft.
  const resetAllGlobals = () =>
    setColors((p) =>
      Object.fromEntries(
        Object.entries(p).map(([t, c]) => [
          t,
          { ...c, hex: c.defaultHex, alpha: c.defaultAlpha, isDefault: true },
        ])
      )
    );

  // ── page-override edits (sparse: presence = overridden; absence = falls to global) ──
  const setPage = (pageId, token, patch, commitHex) => {
    setPages((prev) => {
      const base = prev[pageId]?.[token] ?? { hex: colors[token].hex, alpha: colors[token].alpha };
      return { ...prev, [pageId]: { ...(prev[pageId] || {}), [token]: { ...base, ...patch } } };
    });
    if (commitHex) pushRecent(commitHex);
  };
  const resetPage = (pageId, token) =>
    setPages((prev) => {
      const next = { ...(prev[pageId] || {}) };
      delete next[token];
      const out = { ...prev, [pageId]: next };
      if (!Object.keys(next).length) delete out[pageId];
      return out;
    });
  // Page-wide clear: drop EVERY override for one page (all its rows fall back
  // to Global Style). Distinct from resetPage, which is per-token.
  const clearPage = (pageId) =>
    setPages((prev) => {
      const out = { ...prev };
      delete out[pageId];
      return out;
    });

  const pushRecent = (hex) =>
    setRecents((r) => { const n = pushFront(r, hex, 10); saveLS(LS_RECENTS, n); return n; });
  const saveSwatch = (hex) =>
    setSavedSwatches((s) => { const n = pushFront(s, hex, 24); saveLS(LS_SAVED, n); return n; });
  const removeSwatch = (hex) =>
    setSavedSwatches((s) => { const n = s.filter((x) => x !== hex); saveLS(LS_SAVED, n); return n; });

  // apply a palette swatch to the active row, in the current tab's scope
  const applyPaletteColor = (hex) => {
    if (!activeToken) return;
    if (tab === "pageStyles" && selectedPageId) setPage(selectedPageId, activeToken, { hex }, hex);
    else setGlobal(activeToken, { hex }, hex);
  };
  const activeHex = () => {
    if (!activeToken || !colors) return null;
    if (tab === "pageStyles" && selectedPageId)
      return pages[selectedPageId]?.[activeToken]?.hex ?? colors[activeToken].hex;
    return colors[activeToken].hex;
  };

  const buildInput = () => ({
    globals: Object.entries(colors)
      .filter(([, c]) => !c.isDefault)
      .map(([token, c]) => ({ token, hex: c.hex, alpha: c.alpha ?? null })),
    pages: Object.entries(pages)
      .map(([pageId, toks]) => ({
        pageId,
        colors: Object.entries(toks).map(([token, c]) => ({ token, hex: c.hex, alpha: c.alpha ?? null })),
      }))
      .filter((p) => p.colors.length),
  });

  const save = async () => {
    try {
      const res = await updateDraft({ variables: { input: buildInput() } });
      hydrate(res.data.updateMyDesignDraft);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Save failed: " + e.message);
    }
  };
  const doPublish = async () => {
    setConfirming(false);
    try {
      if (localDirty) await save();
      const res = await publish();
      const { ok, errors } = res.data.publishMyDesign;
      if (!ok) {
        // eslint-disable-next-line no-alert
        alert("Publish failed: " + (errors || []).join(" "));
        return;
      }
      await refetch();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Publish failed: " + e.message);
    }
  };

  const busy = saving || publishing;

  // preview resolver: page override (when on Page Styles for a page) → global → (magenta)
  const previewCss = useMemo(() => {
    if (!colors) return () => "#000";
    const pid = tab === "global" ? null : selectedPageId;
    return (token) => {
      const po = pid && pages[pid]?.[token];
      if (po) return cssColor(po.hex, po.alpha);
      return colors[token] ? cssColor(colors[token].hex, colors[token].alpha) : "#f0f";
    };
  }, [colors, pages, tab, selectedPageId]);

  // Build the preview tree with the mock kit — this also records which tokens the
  // current surface actually uses (powers the "only this page's colors" filter),
  // and glows elements that use the hovered token.
  const { previewTree, usedTokens } = useMemo(() => {
    const mk = makeKit(previewCss, highlightToken);
    const tree = renderMock(tab === "global" ? null : selectedPageId, mk);
    return { previewTree: tree, usedTokens: mk.used };
  }, [previewCss, highlightToken, tab, selectedPageId]);

  const allowedRoles = useMemo(() => allowedRolesFor(Auth.getPermissions()), []);

  if (loading || !colors)
    return (
      <div style={{ ...S.sheet, padding: 24, color: "#eef0f6", maxWidth: 480 }}>
        Loading your theme…
      </div>
    );

  const overriddenCount = (pid) => Object.keys(pages[pid] || {}).length;
  const selectedPage = PAGE_REGISTRY.find((p) => p.id === selectedPageId);

  return (
    <div style={S.sheet}>
      <div style={S.headerBar}>
        <div>
          <h1 style={S.h1}>Theme Designer</h1>
          <p style={S.sub}>
            Restyle your BDR app. Save keeps a draft; Publish pushes it live to your app
            (re-skins within ~a minute). Only affects <b>your</b> login.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={S.badge}>
            {data?.myDesignDraft?.revision ? `Published rev ${data.myDesignDraft.revision}` : "Never published"}
            {localDirty ? " · unsaved" : data?.myDesignDraft?.dirty ? " · unpublished" : ""}
          </span>
          <button style={S.headerGhost} disabled={!localDirty || busy} onClick={save}>{saving ? "Saving…" : "Save draft"}</button>
          <button style={S.headerPrimary} disabled={busy} onClick={() => setConfirming(true)}>{publishing ? "Publishing…" : "Publish"}</button>
        </div>
      </div>

      <div style={S.sheetBody}>
      <div style={S.tabs}>
        {[["global", "Global Style"], ["pages", "Pages"], ["pageStyles", "Page Styles"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...S.tab, ...(tab === k ? S.tabActive : {}) }}
            disabled={k === "pageStyles" && !selectedPageId}
            title={k === "pageStyles" && !selectedPageId ? "Pick a page in the Pages tab first" : undefined}>
            {label}{k === "pageStyles" && selectedPage ? `: ${selectedPage.label}` : ""}
          </button>
        ))}
      </div>

      <div style={S.body}>
        <div>
          {tab === "pages" ? (
            <PagesNavigator
              selectedPageId={selectedPageId}
              overriddenCount={overriddenCount}
              allowedRoles={allowedRoles}
              onPick={(id) => { setSelectedPageId(id); setTab("pageStyles"); }}
            />
          ) : (
            <>
              <PaletteBlock
                activeLabel={activeToken ? TOKEN_META[activeToken]?.label : null}
                activeColor={activeHex()}
                recents={recents}
                saved={savedSwatches}
                onApply={applyPaletteColor}
                onSaveActive={() => { const h = activeHex(); if (h) saveSwatch(h); }}
                onRemoveSaved={removeSwatch}
              />
              <div style={{ ...S.panel, padding: "8px 14px", display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <BdrCheckbox
                  size="sm"
                  label="Only colors on this preview"
                  title="Hide color rows that don't appear on the previewed screen. Colors are still global — this only declutters."
                  checked={filterToPage}
                  onChange={(e) => setFilterToPage(e.target.checked)}
                />
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>{usedTokens.size} used here</span>
                {tab === "global" && (
                  <button
                    style={S.ghost}
                    disabled={!Object.values(colors).some((c) => !c.isDefault)}
                    onClick={() => setConfirmingReset("resetAll")}
                    title="Reset every global color to its default (page overrides are kept)"
                  >
                    Reset all to defaults
                  </button>
                )}
              </div>
              {tab === "pageStyles" && (
                <div style={{ ...S.panel, background: "rgba(90,140,243,0.14)", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13 }}>
                    Editing overrides for <b>{selectedPage?.label}</b>. Unset rows fall back to Global; reset removes the override.
                  </span>
                  <span style={{ flex: 1 }} />
                  {overriddenCount(selectedPageId) > 0 && (
                    <button
                      style={S.ghost}
                      onClick={() => setConfirmingReset({ clearPage: selectedPageId })}
                      title="Remove every override on this page (rows fall back to Global Style)"
                    >
                      Reset this page&apos;s overrides ({overriddenCount(selectedPageId)})
                    </button>
                  )}
                  <button style={S.ghost} onClick={() => setTab("pages")}>← Pages</button>
                </div>
              )}
              {GROUP_ORDER.map((group) => {
                const toks = Object.keys(TOKEN_META).filter(
                  (t) => TOKEN_META[t].group === group && colors[t] && (!filterToPage || usedTokens.has(t))
                );
                if (!toks.length) return null;
                return (
                  <div key={group} style={S.panel}>
                    <div style={S.groupLabel}>{group}</div>
                    {toks.map((token) => {
                      const isPageTab = tab === "pageStyles" && selectedPageId;
                      const override = isPageTab ? pages[selectedPageId]?.[token] : null;
                      const effective = override ?? { hex: colors[token].hex, alpha: colors[token].alpha };
                      const rowIsDefault = isPageTab ? !override : colors[token].isDefault;
                      return (
                        <ColorRow
                          key={token}
                          token={token}
                          meta={TOKEN_META[token]}
                          hex={effective.hex}
                          alpha={effective.alpha}
                          isDefault={rowIsDefault}
                          scopeTag={isPageTab ? (override ? "page" : "from global") : null}
                          active={activeToken === token}
                          onSelect={() => setActiveToken(token)}
                          onHover={setHighlightToken}
                          onChange={(patch, commitHex) =>
                            isPageTab ? setPage(selectedPageId, token, patch, commitHex) : setGlobal(token, patch, commitHex)}
                          onReset={() => (isPageTab ? resetPage(selectedPageId, token) : resetGlobal(token))}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div style={{ position: "sticky", top: 12, alignSelf: "start", maxHeight: "calc(100vh - 24px)", overflowY: "auto" }}>
          <MockPreview
            tree={previewTree}
            pageLabel={tab === "global" ? "Global" : selectedPage?.label || "— pick a page"}
          />
        </div>
      </div>
      </div>

      {confirming && (() => {
        const warnings = contrastWarnings(colors);
        return (
          <div style={S.modalScrim} onClick={() => setConfirming(false)}>
            <div style={S.modal} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginTop: 0 }}>Publish theme?</h3>
              <p style={{ color: "rgba(255,255,255,0.78)" }}>
                This applies the current design (global + per-page) to <b>your</b> live app
                (within ~a minute).{localDirty && " Your unsaved edits will be saved first."}
              </p>
              {warnings.length > 0 && (
                <div style={{ background: "rgba(240,195,109,0.12)", border: "1px solid rgba(240,195,109,0.45)", borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, color: "#f0ce8a" }}>Low-contrast warning</div>
                  <p style={{ fontSize: 12, color: "#f0ce8a", margin: "0 0 6px" }}>
                    These color pairs are harder to read than the defaults. You can publish
                    anyway — this only affects your own app.
                  </p>
                  {warnings.map((w) => (
                    <div key={w.fg + w.bg} style={{ fontSize: 12, color: "#f0ce8a" }}>
                      • {TOKEN_META[w.fg]?.label || w.fg} on {TOKEN_META[w.bg]?.label || w.bg}:{" "}
                      {w.edited.toFixed(2)}:1 (default {w.baseline.toFixed(2)}:1)
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button style={S.ghost} onClick={() => setConfirming(false)}>Cancel</button>
                <button style={S.primary} onClick={doPublish}>
                  {warnings.length ? "Publish anyway" : "Publish live"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {confirmingReset && (
        <div style={S.modalScrim} onClick={() => setConfirmingReset(null)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            {confirmingReset === "resetAll" ? (
              <>
                <h3 style={{ marginTop: 0 }}>Reset all colors to defaults?</h3>
                <p style={{ color: "rgba(255,255,255,0.78)" }}>
                  All {Object.values(colors).filter((c) => !c.isDefault).length} edited
                  global colors go back to their defaults. Page overrides are kept.
                  Nothing is saved until you Save or Publish — reloading this page before
                  saving restores your current draft.
                </p>
              </>
            ) : (
              <>
                <h3 style={{ marginTop: 0 }}>Reset this page&apos;s overrides?</h3>
                <p style={{ color: "rgba(255,255,255,0.78)" }}>
                  Removes all {overriddenCount(confirmingReset.clearPage)} override
                  {overriddenCount(confirmingReset.clearPage) === 1 ? "" : "s"} on{" "}
                  <b>{PAGE_REGISTRY.find((p) => p.id === confirmingReset.clearPage)?.label || confirmingReset.clearPage}</b>.
                  Those rows fall back to Global Style. Nothing is saved until you Save or
                  Publish.
                </p>
              </>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button style={S.ghost} onClick={() => setConfirmingReset(null)}>Cancel</button>
              <button
                style={S.primary}
                onClick={() => {
                  if (confirmingReset === "resetAll") resetAllGlobals();
                  else clearPage(confirmingReset.clearPage);
                  setConfirmingReset(null);
                }}
              >
                {confirmingReset === "resetAll" ? "Reset all" : "Reset page"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PagesNavigator({ selectedPageId, overriddenCount, onPick, allowedRoles }) {
  const visible = PAGE_REGISTRY.filter((p) => allowedRoles.includes(p.role));
  const roles = [...new Set(visible.map((p) => p.role))];
  return (
    <div style={S.panel}>
      <div style={S.groupLabel}>Pages — pick a surface to override its colors</div>
      <p style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 0 }}>
        Only surfaces your role sees in the app are shown. Every override falls back to Global Style unless set here; numbers show how many tokens each page overrides.
      </p>
      {roles.map((role) => (
        <div key={role} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
            {visible.find((p) => p.role === role)?.roleLabel || role}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {visible.filter((p) => p.role === role).map((p) => {
              const n = overriddenCount(p.id);
              const on = selectedPageId === p.id;
              return (
                <button key={p.id} onClick={() => onPick(p.id)}
                  style={{ ...S.pageChip, ...(on ? S.pageChipOn : {}), ...(n ? S.pageChipEdited : {}) }}
                  title={p.id}>
                  {p.label}{n ? ` · ${n}` : ""}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function ColorRow({ token, meta, hex, alpha, isDefault, scopeTag, active, onSelect, onHover, onChange, onReset }) {
  const [hexText, setHexText] = useState(hex);
  useEffect(() => setHexText(hex), [hex]);
  const bad = !/^#[0-9a-fA-F]{6}$/.test(hexText);
  const commitHex = () => {
    if (!bad) { const h = hexText.toUpperCase(); onChange({ hex: h }, h); } else setHexText(hex);
  };

  // Recent-color snapshot on COMMIT only. React's synthetic onChange on
  // <input type=color> maps to the DOM `input` event, which streams every value
  // while you drag across the picker — pushing each throwaway color into Recent
  // and evicting the last real pick. So we live-update the color on `input`
  // (no Recent) and record the Recent only on the native `change` event, which
  // fires when the picker closes. Ref keeps the handler pointed at the latest
  // onChange without re-subscribing the listener on every render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const colorRef = useRef(null);
  useEffect(() => {
    const el = colorRef.current;
    if (!el) return;
    const onCommit = () => { const h = el.value.toUpperCase(); onChangeRef.current({ hex: h }, h); };
    el.addEventListener("change", onCommit);
    return () => el.removeEventListener("change", onCommit);
  }, []);
  return (
    <div onMouseDown={onSelect}
      onMouseEnter={() => onHover?.(token)}
      onMouseLeave={() => onHover?.(null)}
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 4px",
        borderLeft: `3px solid ${active ? "#6f9dff" : "transparent"}`,
        background: active ? "rgba(90,140,243,0.16)" : "transparent", borderRadius: 6 }}>
      <div style={{ width: 34, height: 34, borderRadius: 7, flexShrink: 0, border: "1px solid rgba(255,255,255,0.3)", background: checker, overflow: "hidden" }}>
        <div style={{ width: "100%", height: "100%", background: cssColor(hex, alpha) }} />
      </div>
      <input ref={colorRef} type="color" value={hex} onChange={(e) => onChange({ hex: e.target.value.toUpperCase() })}
        style={{ width: 34, height: 30, padding: 0, border: "none", background: "none", cursor: "pointer" }} title="Pick color" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13 }}>
          {meta.label}
          {scopeTag && <span style={{ ...S.editedTag, color: scopeTag === "page" ? "#8ab4ff" : "rgba(255,255,255,0.4)" }}> · {scopeTag}</span>}
          {!scopeTag && !isDefault && <span style={S.editedTag}> · edited</span>}
        </div>
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)" }}>{token}</div>
      </div>
      <input value={hexText} onChange={(e) => setHexText(e.target.value)} onBlur={commitHex}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} spellCheck={false}
        style={{ width: 84, fontSize: 12, fontFamily: "monospace", background: "rgba(255,255,255,0.08)", color: "#eef0f6", border: "1px solid", borderRadius: 6, padding: "3px 6px", borderColor: bad ? "#e0715a" : "rgba(255,255,255,0.25)" }} />
      <input type="range" min={0} max={255} value={alpha ?? 255} onChange={(e) => onChange({ alpha: Number(e.target.value) })}
        title={`opacity ${Math.round(((alpha ?? 255) / 255) * 100)}%`} style={{ width: 64 }} />
      <button style={S.resetBtn} disabled={isDefault} onClick={onReset} title="Reset to default / remove override">↺</button>
    </div>
  );
}

function PaletteSwatch({ hex, onApply, onRemove }) {
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onApply(hex)} title={`Apply ${hex}`}
        style={{ width: 22, height: 22, borderRadius: 5, border: "1px solid rgba(255,255,255,0.3)", background: hex, cursor: "pointer", padding: 0 }} />
      {onRemove && (
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onRemove(hex)} title="Remove"
          style={{ position: "absolute", top: -5, right: -5, width: 13, height: 13, lineHeight: "10px", borderRadius: "50%", border: "1px solid rgba(255,255,255,0.3)", background: "#fff", fontSize: 10, cursor: "pointer", padding: 0 }}>×</button>
      )}
    </span>
  );
}

function PaletteBlock({ activeLabel, activeColor, recents, saved, onApply, onSaveActive, onRemoveSaved }) {
  return (
    <div style={{ ...S.panel, paddingBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={S.groupLabel}>Palette</span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
          {activeLabel ? (<>Applying to: <b style={{ color: "#8ab4ff" }}>{activeLabel}</b></>) : "Click a color row to select it"}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <button type="button" style={S.ghost} onMouseDown={(e) => e.preventDefault()} onClick={onSaveActive} disabled={!activeColor} title="Save the selected color">+ Save</button>
        {activeColor && <span style={{ width: 18, height: 18, borderRadius: 4, border: "1px solid rgba(255,255,255,0.3)", background: activeColor }} title={activeColor} />}
        {saved.length > 0 && <span style={{ width: 1, height: 18, background: "rgba(255,255,255,0.2)" }} />}
        {saved.map((h) => <PaletteSwatch key={h} hex={h} onApply={onApply} onRemove={onRemoveSaved} />)}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", width: 44 }}>Recent</span>
        {recents.length ? recents.map((h) => <PaletteSwatch key={h} hex={h} onApply={onApply} />) : <span style={{ fontSize: 10, color: "rgba(255,255,255,0.35)" }}>— none yet</span>}
      </div>
    </div>
  );
}

const S = {
  // Dark sheet in the admin family (.bdr-dark-card #1a1a2e) — the light sheet
  // read as a wall of bright white against the wood bg (owner, 2026-07-29).
  // The gradient header stays; body panels are translucent white-on-dark.
  // NOTE: no `overflow: hidden` here — it would make the sheet the sticky scroll
  // container and the phone preview would scroll away with it. Rounded corners are
  // preserved by rounding the header's top corners (below) instead of clipping.
  sheet: { maxWidth: 1200, margin: "16px auto", background: "#1a1a2e", color: "#eef0f6", borderRadius: 14, boxShadow: "0 10px 34px rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.2)" },
  // Blue→purple gradient header, matching the admin nav + .bdr-gradient-panel.
  headerBar: { background: "linear-gradient(135deg, #1565c0, #7b1fa2)", padding: "16px 20px", borderRadius: "14px 14px 0 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" },
  h1: { margin: 0, color: "#fff", fontSize: 24 },
  sub: { margin: "3px 0 0", color: "rgba(255,255,255,0.85)", fontSize: 13, maxWidth: 640 },
  sheetBody: { padding: "10px 18px 20px" },
  badge: { fontSize: 12, padding: "4px 10px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.5)", color: "#fff" },
  headerGhost: { padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.14)", color: "#fff", cursor: "pointer", fontSize: 13 },
  headerPrimary: { padding: "6px 16px", borderRadius: 8, border: "none", background: "#fff", color: "#1565c0", fontWeight: 700, cursor: "pointer", fontSize: 13 },
  ghost: { padding: "5px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.28)", background: "rgba(255,255,255,0.08)", color: "#eef0f6", cursor: "pointer", fontSize: 13 },
  primary: { padding: "5px 14px", borderRadius: 8, border: "none", background: "#1565c0", color: "#fff", cursor: "pointer", fontSize: 13 },
  tabs: { display: "flex", gap: 4, marginTop: 4, borderBottom: "1px solid rgba(255,255,255,0.15)", flexWrap: "wrap" },
  tab: { padding: "8px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 14, color: "rgba(255,255,255,0.65)", borderBottom: "2px solid transparent" },
  tabActive: { color: "#8ab4ff", borderBottom: "2px solid #8ab4ff", fontWeight: 600 },
  body: { display: "grid", gridTemplateColumns: "minmax(360px, 1fr) minmax(300px, 360px)", gap: 20, marginTop: 14, alignItems: "start" },
  panel: { border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, padding: "12px 14px", marginBottom: 12, background: "rgba(255,255,255,0.05)" },
  groupLabel: { fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "rgba(255,255,255,0.5)", marginBottom: 6 },
  editedTag: { fontSize: 10, color: "#8ab4ff" },
  resetBtn: { padding: "2px 8px", fontSize: 11, borderRadius: 6, border: "1px solid rgba(255,255,255,0.28)", background: "rgba(255,255,255,0.08)", color: "#eef0f6", cursor: "pointer" },
  pageChip: { padding: "5px 10px", borderRadius: 14, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.07)", color: "#e6e9f2", cursor: "pointer", fontSize: 12 },
  pageChipOn: { borderColor: "#6f9dff", background: "rgba(90,140,243,0.25)", color: "#cfe0ff", fontWeight: 600 },
  pageChipEdited: { borderColor: "rgba(154,190,255,0.6)", background: "rgba(90,140,243,0.14)" },
  modalScrim: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "#232840", color: "#eef0f6", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, padding: 20, width: 440, maxWidth: "90vw", boxShadow: "0 12px 40px rgba(0,0,0,0.5)" },
};
